import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner, Alert, Form, Badge } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSave, faCheckCircle, faPlus, faEdit, faTrash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useToast } from '../../contexts/ToastContext';
import SchoolUserService from '../../services/schoolUser.service';
import ClassContentService from '../../services/classContent.service';
import api from '../../services/api';

const TeacherClassContent = () => {
  const { user } = useAuth();
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const { showToast } = useToast();

  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCompetences, setSelectedCompetences] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [description, setDescription] = useState('');
  const [existingContents, setExistingContents] = useState([]);
  const [editingContentId, setEditingContentId] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchTeacherSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedYear]);

  useEffect(() => {
    if (selectedSubject) {
      fetchCompetences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedClassroom && selectedSubject && selectedDate) {
      checkExistingContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassroom, selectedSubject, selectedDate]);

  const fetchTeacherSubjects = async () => {
    try {
      setLoading(true);
      const response = await SchoolUserService.getTeacherAssignments({
        school: selectedSchool,
        year: selectedYear
      });
      setTeacherSubjects(response.results || response || []);
    } catch (err) {
      console.error('Error fetching teacher subjects:', err);
      setError('Erro ao carregar disciplinas do professor.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetences = async () => {
    try {
      const response = await api.get('/competences/', {
        params: { subject: selectedSubject }
      });
      const competencesList = response.data.results || response.data || [];
      setCompetences(competencesList);

      // Buscar habilidades para todas as competências
      const allSkills = [];
      for (const comp of competencesList) {
        const skillsResponse = await api.get('/skills/', {
          params: { competence: comp.id }
        });
        const skillsList = skillsResponse.data.results || skillsResponse.data || [];
        allSkills.push(...skillsList);
      }
      setSkills(allSkills);
    } catch (err) {
      console.error('Error fetching competences:', err);
    }
  };

  const checkExistingContent = async () => {
    try {
      const response = await ClassContentService.getAll({
        classroom: selectedClassroom,
        subject: selectedSubject,
        date: selectedDate
      });

      const contents = response.results || response || [];
      setExistingContents(contents);
      setIsAddingNew(false);
      setEditingContentId(null);
      setSelectedCompetences([]);
      setSelectedSkills([]);
      setDescription('');
    } catch (err) {
      console.error('Error checking existing content:', err);
    }
  };

  const handleClassroomChange = (e) => {
    setSelectedClassroom(e.target.value);
    setSelectedSubject('');
    setSelectedCompetences([]);
    setSelectedSkills([]);
    setDescription('');
    setExistingContents([]);
    setEditingContentId(null);
    setIsAddingNew(false);
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
    setSelectedCompetences([]);
    setSelectedSkills([]);
    setDescription('');
    setExistingContents([]);
    setEditingContentId(null);
    setIsAddingNew(false);
  };

  const handleCompetenceToggle = (competenceId) => {
    setSelectedCompetences(prev => {
      if (prev.includes(competenceId)) {
        return prev.filter(id => id !== competenceId);
      } else {
        return [...prev, competenceId];
      }
    });
  };

  const handleSkillToggle = (skillId) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillId)) {
        return prev.filter(id => id !== skillId);
      } else {
        return [...prev, skillId];
      }
    });
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingContentId(null);
    setSelectedCompetences([]);
    setSelectedSkills([]);
    setDescription('');
  };

  const handleEdit = (content) => {
    setIsAddingNew(false);
    setEditingContentId(content.id);
    setSelectedCompetences(content.competences || []);
    setSelectedSkills(content.skills || []);
    setDescription(content.description || '');
  };

  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setEditingContentId(null);
    setSelectedCompetences([]);
    setSelectedSkills([]);
    setDescription('');
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro de aula?')) {
      return;
    }

    try {
      await ClassContentService.delete(contentId);
      showToast('Conteúdo da aula excluído com sucesso!', 'success');
      await checkExistingContent();
    } catch (err) {
      console.error('Error deleting class content:', err);
      showToast('Erro ao excluir conteúdo da aula.', 'error');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const data = {
        school: selectedSchool,
        teacher: user?.id,
        classroom: selectedClassroom,
        subject: selectedSubject,
        date: selectedDate,
        competences: selectedCompetences,
        skills: selectedSkills,
        description: description
      };

      if (editingContentId) {
        await ClassContentService.update(editingContentId, data);
        showToast('Conteúdo da aula atualizado com sucesso!', 'success');
      } else {
        await ClassContentService.create(data);
        showToast('Conteúdo da aula registrado com sucesso!', 'success');
      }

      await checkExistingContent();
    } catch (err) {
      console.error('Error saving class content:', err);
      showToast('Erro ao salvar conteúdo da aula.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getUniqueClassrooms = () => {
    const classrooms = new Map();
    teacherSubjects.forEach(ts => {
      const fullName = `${ts.classroom_grade} - ${ts.classroom_education_level} - ${ts.classroom_name}`;
      if (!classrooms.has(ts.classroom_id)) {
        classrooms.set(ts.classroom_id, {
          id: ts.classroom_id,
          name: fullName
        });
      }
    });
    return Array.from(classrooms.values());
  };

  const getSubjectsForClassroom = () => {
    if (!selectedClassroom) return [];
    return teacherSubjects
      .filter(ts => `${ts.classroom_id}` === selectedClassroom)
      .map(ts => ({
        id: ts.subject_id,
        name: ts.subject_name
      }));
  };

  const getSkillsForCompetence = (competenceId) => {
    return skills.filter(skill => skill.competence === competenceId);
  };

  const canSave = selectedClassroom && selectedSubject && selectedDate;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Registrar Aula - {selectedYear}</h4>
          <p className="mb-0">Registre o conteúdo ministrado em cada aula</p>
        </div>
      </div>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Turma *</Form.Label>
                    <Form.Select
                      value={selectedClassroom}
                      onChange={handleClassroomChange}
                      required
                    >
                      <option value="">Selecione uma turma</option>
                      {getUniqueClassrooms().map((classroom) => (
                        <option key={classroom.id} value={classroom.id}>
                          {classroom.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Disciplina *</Form.Label>
                    <Form.Select
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      disabled={!selectedClassroom}
                      required
                    >
                      <option value="">Selecione uma disciplina</option>
                      {getSubjectsForClassroom().map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Data da Aula *</Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      max={today}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Aulas Existentes */}
      {selectedClassroom && selectedSubject && selectedDate && existingContents.length > 0 && !isAddingNew && !editingContentId && (
        <Row className="mb-3">
          <Col xs={12}>
            <Card border="light" className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Aulas Registradas ({existingContents.length})</h6>
                <Button variant="primary" size="sm" onClick={handleAddNew}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Nova Aula
                </Button>
              </Card.Header>
              <Card.Body>
                {existingContents.map((content, index) => (
                  <Alert key={content.id} variant="light" className="border mb-2">
                    <Row className="align-items-center">
                      <Col>
                        <strong>Aula {index + 1}</strong>
                        <div className="small text-muted mt-1">
                          {content.competences?.length || 0} competências, {content.skills?.length || 0} habilidades
                        </div>
                        {content.description && (
                          <div className="small mt-1">{content.description.substring(0, 100)}...</div>
                        )}
                      </Col>
                      <Col xs="auto">
                        <Button variant="info" size="sm" className="me-2" onClick={() => handleEdit(content)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(content.id)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </Col>
                    </Row>
                  </Alert>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Botão para adicionar primeira aula */}
      {selectedClassroom && selectedSubject && selectedDate && existingContents.length === 0 && !isAddingNew && !editingContentId && (
        <Row className="mb-3">
          <Col xs={12}>
            <Alert variant="info">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Nenhuma aula registrada para esta data.
              <Button variant="primary" size="sm" className="ms-3" onClick={handleAddNew}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Registrar Aula
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Formulário de Conteúdo da Aula */}
      {selectedSubject && (isAddingNew || editingContentId) && (
        <Row>
          <Col xs={12}>
            <Card border="light" className="shadow-sm">
              <Card.Header>
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0">
                      <FontAwesomeIcon icon={faPencilAlt} className="me-2" />
                      {editingContentId ? 'Editar Conteúdo da Aula' : 'Registrar Nova Aula'}
                    </h5>
                  </Col>
                  <Col className="text-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="me-2"
                      onClick={handleCancelEdit}
                    >
                      <FontAwesomeIcon icon={faTimes} className="me-1" />
                      Cancelar
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSave}
                      disabled={!canSave || saving}
                    >
                      <FontAwesomeIcon icon={faSave} className="me-1" />
                      {saving ? 'Salvando...' : (editingContentId ? 'Atualizar' : 'Salvar')}
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                  </div>
                )}
                {error && (
                  <Alert variant="danger">
                    {error}
                  </Alert>
                )}
                {!loading && !error && (
                  <>
                    {/* Competências */}
                    <div className="mb-4">
                      <h6 className="mb-3">Competências Trabalhadas</h6>
                      {competences.length === 0 ? (
                        <Alert variant="info">
                          Nenhuma competência cadastrada para esta disciplina.
                        </Alert>
                      ) : (
                        <div className="d-flex flex-wrap gap-2">
                          {competences.map((competence) => (
                            <Badge
                              key={competence.id}
                              bg={selectedCompetences.includes(competence.id) ? 'primary' : 'secondary'}
                              className="p-2"
                              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                              onClick={() => handleCompetenceToggle(competence.id)}
                            >
                              {competence.code} - {competence.description || 'Sem descrição'}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Habilidades */}
                    <div className="mb-4">
                      <h6 className="mb-3">Habilidades Trabalhadas</h6>
                      {skills.length === 0 ? (
                        <Alert variant="info">
                          Nenhuma habilidade cadastrada para as competências desta disciplina.
                        </Alert>
                      ) : (
                        <>
                          {competences.map((competence) => {
                            const competenceSkills = getSkillsForCompetence(competence.id);
                            if (competenceSkills.length === 0) return null;

                            return (
                              <div key={competence.id} className="mb-3">
                                <small className="text-muted d-block mb-2">
                                  <strong>{competence.code}</strong> - {competence.description}
                                </small>
                                <div className="d-flex flex-wrap gap-2">
                                  {competenceSkills.map((skill) => (
                                    <Badge
                                      key={skill.id}
                                      bg={selectedSkills.includes(skill.id) ? 'success' : 'light'}
                                      text={selectedSkills.includes(skill.id) ? 'white' : 'dark'}
                                      className="p-2"
                                      style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                                      onClick={() => handleSkillToggle(skill.id)}
                                    >
                                      {skill.code} - {skill.description || 'Sem descrição'}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>

                    {/* Descrição */}
                    <div>
                      <h6 className="mb-3">Descrição Adicional (Opcional)</h6>
                      <Form.Group>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Descreva brevemente o conteúdo ministrado nesta aula..."
                        />
                      </Form.Group>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default TeacherClassContent;
